import React from 'react';
import { Subtask } from 'shovel-lib/types';

// Subtasks in the Task List, as ClickUp shows them (Petr: "just copy them"): an arrow before the ring opens a task's
// subtasks as lines under it, the counts do the same, and "+" adds one on a line there. "Expand subtasks" (⋮ menu) opens
// every task's; the arrow still opens or closes one task's either way. What shows, and how tall it is, so the list gives
// each task's row the room (getItemSizes).

/** One subtask's line under its task. */
export const SUBTASK_LINE_HEIGHT = 28;
/** Air under a task's last subtask line, before the next task. */
export const SUBTASK_BLOCK_PADDING = 6;

export type SubtaskView = {
  /** This list shows subtasks as lines (the Task List, not its Trash). Off, the counts open popups, as before. */
  enabled: boolean;
  /** "Expand subtasks": every task's are open, unless its arrow closed them. */
  expandAll: boolean;
  /** Tasks whose arrow went the other way from expandAll: opened while collapsed, or closed while expanded. */
  flipped: Set<number>;
  /** The task with its "new subtask" line open, if any. */
  addingTo: number | null;
  toggleExpanded: (taskId: number) => void;
  startAdding: (taskId: number) => void;
  stopAdding: () => void;
};

export const SubtaskViewContext = React.createContext<SubtaskView>({
  enabled: false,
  expandAll: false,
  flipped: new Set(),
  addingTo: null,
  toggleExpanded: () => undefined,
  startAdding: () => undefined,
  stopAdding: () => undefined
});

type TaskLike = { id: number; subtasks?: Subtask[] | null };
type ViewState = Pick<SubtaskView, 'enabled' | 'expandAll' | 'flipped' | 'addingTo'>;

/** A task's subtasks are open under it. */
export const isExpanded = (taskId: number, view: Pick<SubtaskView, 'enabled' | 'expandAll' | 'flipped'>) =>
  view.enabled && view.expandAll !== view.flipped.has(taskId);

/** The subtasks a task shows as lines: the open ones, then the done ones crossed out, each in their own order. */
export const shownSubtasks = (subtasks: Subtask[] | null | undefined): Subtask[] => {
  const list = [...(subtasks || [])].sort((a, b) => (a.ordinalNumber ?? 0) - (b.ordinalNumber ?? 0));
  return [...list.filter(s => !s.completed), ...list.filter(s => s.completed)];
};

/** How many lines show under a task: its subtasks (when open) and the "new subtask" line while it's open. */
export const subtaskLineCount = (task: TaskLike, view: ViewState) => {
  if (!view.enabled) return 0;
  const adding = view.addingTo === task.id ? 1 : 0;
  if (!isExpanded(task.id, view)) return adding;
  return (task.subtasks?.length || 0) + adding;
};

/** The room a task's subtask lines take under it (0 with none). */
export const subtaskBlockHeight = (task: TaskLike, view: ViewState) => {
  const lines = subtaskLineCount(task, view);
  return lines ? lines * SUBTASK_LINE_HEIGHT + SUBTASK_BLOCK_PADDING : 0;
};

/**
 * Where a dragged subtask line lands, moved this far (px): by whole lines, kept among its own kind - the open ones, or the
 * done ones under them - as the lines always show open ones first.
 */
export const subtaskDropIndex = (from: number, dy: number, openCount: number, total: number) => {
  const [min, max] = from < openCount ? [0, openCount - 1] : [openCount, total - 1];
  return Math.min(max, Math.max(min, from + Math.round(dy / SUBTASK_LINE_HEIGHT)));
};

/** The lines' subtasks with one moved, numbered in their new order (what the server keeps). */
export const movedSubtasks = (lines: Subtask[], from: number, to: number): Subtask[] => {
  const list = [...lines];
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list.map((subtask, index) => ({ ...subtask, ordinalNumber: index + 1 }));
};
